import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ServiceGroups } from '../components/ui/ServiceGroups';
import { CtaBand } from '../components/ui/CtaBand';
import { Faq } from '../components/ui/Faq';
import { HSE_GROUPS, HSE_TRAININGS, IMAGES } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Qué incluye una auditoría HSE de Rabemar?',
  answer: 'Relevamiento de cumplimiento contra la matriz legal aplicable, revisión de documentación, condiciones de trabajo, estado de unidades y equipamiento, y un informe con hallazgos priorizados y plan de acción.'
}, {
  question: '¿Se auditan también los transportistas y subcontratistas?',
  answer: 'Sí. La evaluación de subcontratistas y las auditorías a transportistas forman parte del alcance, junto con la gestión de incumplimientos detectados.'
}, {
  question: '¿Rabemar dicta las capacitaciones?',
  answer: 'Sí. El programa incluye manejo defensivo, RCP, primeros auxilios, uso de extintores, control de emergencias, incidentes en tránsito, derrames, incendio, fugas, sistemas TMS/WMS e inducciones de clientes.'
}, {
  question: '¿Qué ocurre ante un incidente?',
  answer: 'Se activa el protocolo de respuesta, se investiga el accidente o incidente, se determinan causas y se definen planes de acción con responsables y plazos, con seguimiento hasta el cierre.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'HSE & Seguridad',
  path: '/soluciones/hse'
}];
export function Hse() {
  useSeo({
    title: 'HSE Argentina | Seguridad, higiene y medio ambiente en logística — Rabemar',
    description: 'Auditorías HSE, matriz legal de seguridad y medio ambiente, control de cumplimiento, capacitaciones, EPP, checklist de unidades e investigación de accidentes.',
    path: '/soluciones/hse',
    image: IMAGES.hse,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('HSE — Salud, Seguridad y Medio Ambiente', 'Auditorías HSE, seguimiento de legislación, matriz legal, capacitaciones, EPP, mantenimiento preventivo y correctivo, gestión de incidentes y respuesta ante emergencias.', '/soluciones/hse'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="05 — HSE & Seguridad" title={<>
            La seguridad no se audita al final.
            <br data-id="element-716" data-mp="1u3rcp1v2p41h" />
            Se integra a la operación desde el comienzo.
          </>} lead="Cumplimiento, capacitación, control de unidades y gestión de incidentes trabajados como parte de la operación diaria, no como un trámite posterior." image={IMAGES.hse} imageAlt="Inspector de seguridad con chaleco reflectante y casco revisando un neumático y un extintor en un playón industrial" crumbs={CRUMBS} ctaLabel="Solicitar evaluación HSE" ctaService="HSE" data-id="element-715" data-mp="1keg2td0d3vcj" />

      <section className="bg-white py-20 md:py-28" data-id="element-717" data-mp="3d2xo9l0fhj5p">
        <Container data-id="element-718" data-mp="3d4igv536ms0x">
          <div className="max-w-3xl" data-id="element-719" data-mp="2ydjc655gwnmm">
            <Eyebrow data-id="element-720" data-mp="2yeko0bub5e5e">Alcance HSE</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-721" data-mp="2ybgd6vmc7k5r">
              Del marco legal al control en el playón.
            </Heading>
            <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-722" data-mp="3r1owwrwiucmg">
              Rabemar traduce la normativa aplicable en controles concretos sobre personas, unidades, proveedores e
              instalaciones, con evidencia documentada de cada verificación.
            </p>
          </div>
          <div className="mt-14" data-id="element-723" data-mp="3m2kow4lc2kxp">
            <ServiceGroups groups={HSE_GROUPS} columns={3} data-id="element-724" data-mp="3m1spsjsj252o" />
          </div>
        </Container>
      </section>

      <section className="bg-graphite-900 py-20 md:py-28" data-id="element-725" data-mp="26kserrc1ssvs">
        <Container data-id="element-726" data-mp="2aogmqb4ldqhk">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-727" data-mp="2aoqbxwhtbtm7">
            <div data-id="element-728" data-mp="2qvd10ctbv9tu">
              <Eyebrow tone="dark" data-id="element-729" data-mp="2qvv67yxywydw">Capacitaciones</Eyebrow>
              <Heading as="h2" size="md" tone="dark" className="mt-5" data-id="element-730" data-mp="2m4mwf1sq7b07">
                El equipo entrenado antes del incidente.
              </Heading>
              <p className="mt-6 text-base leading-relaxed text-mist-200" data-id="element-731" data-mp="2h5tg71m215wc">
                Programa de capacitación aplicado a la operación real: transporte, depósito, planta y oficinas, con
                registro de asistencia y renovación según frecuencia requerida.
              </p>
            </div>
            <ul className="grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-732" data-mp="34vnt1j6diugl">
              {HSE_TRAININGS.map(training => <li key={training} className="bg-graphite-900 px-5 py-4 text-sm text-mist-200" data-id="element-733" data-mp="2vu6fa7gfv4v2">
                  {training}
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-734" data-mp="1kerib3o8cczy">
        <Container data-id="element-735" data-mp="1khdqn5iy2wj8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-736" data-mp="3w2mm0wcnxn3s">
            <div data-id="element-737" data-mp="3w0jm9klpkflv">
              <Eyebrow data-id="element-738" data-mp="3yamyirtzwh0">Control de unidades</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-739" data-mp="3z3jys0zn7ly">
                Nada sale a la ruta sin verificación.
              </Heading>
            </div>
            <div className="grid gap-px bg-mist-200 sm:grid-cols-2" data-id="element-740" data-mp="dowx3xgs3usb">
              {[{
              t: 'Checklist de unidades',
              d: 'Verificación previa documentada de cada unidad antes de operar.'
            }, {
              t: 'Control de extintores',
              d: 'Estado, vigencia y disponibilidad del equipamiento de emergencia.'
            }, {
              t: 'Control de neumáticos',
              d: 'Condición y desgaste como parte del control preventivo.'
            }, {
              t: 'Mantenimiento preventivo y correctivo',
              d: 'Plan de intervención asociado al uso real de la unidad.'
            }].map(item => <div key={item.t} className="bg-white p-7" data-id="element-741" data-mp="2yecgw8hkyj8g">
                  <h3 className="font-display text-lg font-bold tracking-tight text-graphite-900" data-id="element-742" data-mp="2yeulbseec3rg">{item.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-graphite-500" data-id="element-743" data-mp="5lyussbc5vyy">{item.d}</p>
                </div>)}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-744" data-mp="eipncuqoisos">
        <Container data-id="element-745" data-mp="9ko52c12uxed">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-746" data-mp="9k60ms49hcvd">
            <div data-id="element-747" data-mp="ohye7eq8zr6x">
              <Eyebrow data-id="element-748" data-mp="ohf60pqegh63">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-749" data-mp="jk56gh9aqp4j">
                HSE & Seguridad
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-750" data-mp="xa8rj4wfwcse" />
          </div>
        </Container>
      </section>

      <CtaBand title="Solicite una evaluación HSE." text="Revisamos cumplimiento, documentación, condiciones operativas y capacitación, y devolvemos hallazgos priorizados con plan de acción." primaryLabel="Solicitar evaluación HSE" location="hse-cta" service="HSE" data-id="element-751" data-mp="t9gdzzgjblqb" />
    </>;
}